import { useContext } from 'react';
import { ToastContext } from './ToastProvider';
import type { ToastContextValue } from './ToastProvider';

/**
 * Returns `{ showToast }` for triggering toasts imperatively from anywhere
 * in the component tree.
 *
 * Must be called inside a component that is a descendant of `<ToastProvider>`.
 * Throws if called outside a provider — this is intentional so misconfiguration
 * surfaces immediately rather than failing silently.
 *
 * @example
 * const { showToast } = useToast();
 *
 * // Auto-dismiss after 5s (default)
 * showToast({ variant: 'success', message: 'Profile saved.' });
 *
 * // With dismiss button and custom duration
 * showToast({ variant: 'error', message: 'Upload failed.', showDismiss: true, duration: 8000 });
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === undefined) {
    throw new Error(
      'useToast must be called inside a <ToastProvider>. ' +
        'Wrap your app (or the relevant subtree) with <ToastProvider>.'
    );
  }
  return ctx;
}
